import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FlaskConical, ShieldCheck, Users, Eye, Crown, Lock, Truck, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";

const ROLES = [
  { key: "owner", label: "Enter as Owner", icon: Crown, desc: "Full access — see everything, including Billing & owner settings." },
  { key: "staff", label: "Preview as Staff", icon: Users, desc: "A standard staff member. Cannot see Billing, pricing or owner settings." },
  { key: "viewer", label: "Preview as Viewer", icon: Eye, desc: "Read-only. Cannot change anything, and cannot see Billing or owner settings." },
];

const CHECKS = [
  { icon: Lock, text: "Standard users (Staff / Viewer) cannot see Billing, subscription pricing, Super User settings or the owner email." },
  { icon: ShieldCheck, text: "Company data is strictly isolated — the test company can never see your real company's drivers, vehicles, files or uploads (and vice-versa)." },
  { icon: Truck, text: "Plan limits trigger correctly: 10 vehicles, 5 office users, unlimited trailers & drivers. Try adding past the vehicle cap to see the block." },
];

export default function TestMode() {
  const { enterSandbox, resetSandbox } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const enter = async (role) => {
    setBusy(role);
    try {
      await enterSandbox(role);
      toast.success("Entered Test Mode — your live data is safe");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Test Mode");
      setBusy(null);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset the test company back to its sample data?")) return;
    setBusy("reset");
    try { await resetSandbox(); toast.success("Test data reset to sample fleet"); }
    catch { toast.error("Could not reset test data"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="test-mode-page" className="max-w-4xl">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1 flex items-center gap-2">
          <FlaskConical className="text-violet-600" size={30} /> Test Mode
        </h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          A safe, isolated sandbox company for you to test anything — permissions, limits and what standard users can and cannot see —
          <b> without ever touching your live data.</b> Use it any time, as often as you like.
        </p>
      </div>

      <div data-testid="isolation-confirmation" className="rounded-md border-2 border-emerald-200 bg-emerald-50 px-5 py-4 mb-6">
        <p className="flex items-center gap-2 font-heading font-bold text-emerald-800"><ShieldCheck size={18} /> Your data is safe &amp; private</p>
        <p className="text-sm text-emerald-800/90 mt-1.5">
          Every company's data is stored and queried strictly by its own account ID at the database level. <b>One company can never see
          another company's drivers, vehicles, documents or uploads</b> — that includes this test company and your real one. Test Mode runs
          in a completely separate company, so nothing you do here can affect, delete or expose your live information.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {ROLES.map((r) => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-md p-4 flex flex-col">
            <r.icon size={20} className="text-violet-600 mb-2" />
            <p className="font-semibold text-slate-900">{r.label.replace("Enter as ", "").replace("Preview as ", "")}</p>
            <p className="text-xs text-slate-500 mt-1 flex-1">{r.desc}</p>
            <Button data-testid={`enter-sandbox-${r.key}`} onClick={() => enter(r.key)} disabled={!!busy}
              className="mt-3 rounded-md bg-violet-600 hover:bg-violet-700 text-white gap-2">
              {busy === r.key ? <Loader2 size={15} className="animate-spin" /> : <r.icon size={15} />} {r.label}
            </Button>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6">
        <p className="font-heading font-bold text-slate-900 mb-3">What you can verify in the sandbox</p>
        <ul className="space-y-3">
          {CHECKS.map((c, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
              <c.icon size={17} className="text-slate-400 shrink-0 mt-0.5" /> <span>{c.text}</span>
            </li>
          ))}
          <li className="flex items-start gap-3 text-sm text-slate-700">
            <CheckCircle2 size={17} className="text-slate-400 shrink-0 mt-0.5" />
            <span>Invite a test user (Office &rsaquo; Team), then flip the role banner at the top to <b>Staff</b> or <b>Viewer</b> to see exactly what they see.</span>
          </li>
        </ul>
      </div>

      <div className="flex items-center gap-3">
        <Button data-testid="reset-sandbox-page" variant="outline" onClick={reset} disabled={!!busy} className="rounded-md gap-2">
          {busy === "reset" ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Reset test data
        </Button>
        <span className="text-xs text-slate-400">Wipes the test company back to a small sample fleet. Only affects Test Mode.</span>
      </div>
    </div>
  );
}
